<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { AdminUser } from '../lib/admin';
  import AdminSidebar from './AdminSidebar.svelte';
  import TrialBanner from '../lib/ui/TrialBanner.svelte';
  import CommunityLogo from '../lib/ui/CommunityLogo.svelte';
  import CommunitySelect from '../lib/ui/CommunitySelect.svelte';
  import RosterAccessBanner from '../lib/ui/RosterAccessBanner.svelte';
  import ExpiredPaywall from '../lib/ui/ExpiredPaywall.svelte';
  import { activeCommunity, community, optimisticSkoolId } from '../lib/community.svelte';
  import { ensureRoles } from '../lib/roles.svelte';

  let { me, children }: { me: AdminUser | null; children: Snippet } = $props();

  let mobileOpen = $state(false);

  // The community the panel is scoped to, and whether its trial is over. When it
  // is, we gate the body here (not a full-page redirect) so the sidebar's
  // community switcher stays reachable — a creator holding other live communities
  // can switch away. The all-expired case is still covered by the server 402.
  const active = $derived(activeCommunity());
  // Billing is a CREATOR concern only — superadmins are Sideguide staff who browse
  // every community and must never be paywalled or shown a trial/upgrade prompt.
  const isCreator = $derived(me?.role === 'creator');
  // Gate the body when the ACTIVE community needs paying for — `expired` (a lapsed
  // subscription) OR `needs_payment` (over the free cap, never subscribed: a fresh
  // over-cap onboard, a free community that grew past the cap, or a 2nd community an
  // owner added). Other live communities stay reachable via the sidebar switcher.
  const gated = $derived(
    isCreator && (active?.plan_status === 'expired' || active?.plan_status === 'needs_payment'),
  );

  // Load the community's member-role map so every avatar can wear its role ring.
  //
  // Issued at RTT 1, in parallel with the bootstrap, against the id already in
  // localStorage — this used to wait for `community.loaded`, which made the role
  // map the THIRD round trip of a cold load (doc 02 §2.1 row 3). The cold-Overview
  // target of "3 requests, 1 RTT" is /v1/me + the page's own fetch + this one.
  //
  // `optimisticSkoolId()` returns the live selection once the bootstrap has landed
  // and the stored id before it, so this effect re-runs with the resolved id if the
  // stored one was stale. `ensureRoles` is idempotent per community and fail-soft
  // (a 403 on a stale id just leaves avatars ringless until the re-run), so the two
  // passes cost one request in the normal case and two in the recovery case.
  $effect(() => {
    void ensureRoles(optimisticSkoolId());
  });
</script>

<div class="flex min-h-screen">
  <!-- Desktop rail: sticky, full height, scrolls with the viewport -->
  <aside class="sticky top-0 hidden h-screen shrink-0 border-r hair lg:block">
    <AdminSidebar {me} />
  </aside>

  <!-- Mobile drawer -->
  {#if mobileOpen}
    <div class="fixed inset-0 z-40 lg:hidden">
      <button class="absolute inset-0 bg-black/50" aria-label="Close menu" onclick={() => (mobileOpen = false)}></button>
      <div class="absolute inset-y-0 left-0 shadow-2xl shadow-black/40">
        <AdminSidebar {me} mobile onNavigate={() => (mobileOpen = false)} />
      </div>
    </div>
  {/if}

  <!-- Content column: a subtle grey canvas so white cards read as raised. -->
  <div class="flex min-w-0 flex-1 flex-col bg-base-200">
    <!-- Mobile-only top bar with the menu trigger -->
    <header
      class="sticky top-0 z-20 flex items-center gap-2 border-b hair bg-base-100/85 px-4 py-2.5 backdrop-blur-md lg:hidden"
    >
      <button class="btn btn-ghost btn-sm btn-square" aria-label="Open menu" onclick={() => (mobileOpen = true)}>
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <a href="/admin/overview" class="flex items-center gap-2">
        <img src="/icon-128.png" alt="" class="h-6 w-6 rounded" />
        <span class="font-display text-sm font-bold tracking-tight">Sideguide</span>
      </a>
    </header>

    <!-- Which community you are looking at — on EVERY admin page, defined once here
         rather than in each page's own heading, because twenty pages rebuilding the same
         strip is the duplication this app keeps deleting.

         This strip and the page heading below it read as one block, so they must start
         at the same x — and that only holds if neither of them is centred. Both are
         full-bleed now and both take `px-5 sm:px-6`, so the agreement is a constant
         rather than something to negotiate per page. Two earlier attempts negotiated it:
         pinning this to a fixed cap left the community name a third of a screen left of
         the title beneath it, and making it follow the page's cap fixed that by making
         the SELECTOR jump on every navigation instead.

         Deliberately NOT an `<h1>`: every page already owns one for its own title, and
         a second would leave the document with two top-level headings. This is chrome
         that names the subject — the same job the sidebar's switcher does, which is
         why that is not a heading either.

         Hidden when nothing is selected (a superadmin before the fleet list lands),
         because an empty logo tile next to a blank name reads as a broken image. -->
    {#if active}
      <!-- `min-h`, not `h`: the single-line case lands exactly on `--app-header-h` so the
           border lines up with the sidebar's brand block beside it, and a narrow viewport
           that wraps the chips grows instead of clipping them. `px-5 sm:px-6` is
           `PageContainer`'s padding, which is the whole alignment contract. -->
      <div
        class="flex min-h-[var(--app-header-h)] flex-wrap items-center gap-x-3 gap-y-2
               border-b hair bg-base-100 px-5 py-1.5 sm:px-6"
      >
        {#if community.list.length > 1}
          <!-- The switcher IS the heading. It moved here from the sidebar: it names the
               community, and so did the static heading that used to sit in this spot —
               two controls for one piece of state, one of them a plain label. -->
          <CommunitySelect />
        {:else}
          <!-- Exactly one community: there is nothing to switch to, so a dropdown would
               be a control that cannot do anything. Same logo and type as the trigger. -->
          <div class="flex min-w-0 items-center gap-2.5">
            <CommunityLogo name={active.name} logoUrl={active.logo_url} size="h-7 w-7" textClass="text-xs" />
            <span class="redact min-w-0 truncate font-display text-base font-bold tracking-tight">{active.name}</span>
          </div>
        {/if}

        <!-- Community-scoped notices, in the header rather than as full-width strips
             above it. Both are ABOUT this community, so this is where they belong —
             and being here is what let their copy drop the community's name, which
             the strips had to repeat because they floated above a page that never
             said which community it was about.

             `ml-auto` pushes them to the right of the name; `flex-wrap` on the row
             above drops them to a second line rather than crushing the name when a
             narrow viewport cannot fit both.

             The trial chip stays CREATOR-ONLY — superadmins are staff and are never on
             a plan. The roster chip is not gated that way on purpose: a superadmin
             browsing a community we can't read the member list for needs to know why
             the membership columns are empty, same as its owner does. -->
        <div class="ml-auto flex flex-wrap items-center gap-2">
          {#if isCreator}<TrialBanner />{/if}
          <RosterAccessBanner />
        </div>
      </div>
    {/if}

    {#if gated && active}
      <!-- Active community's trial is over — gate its data behind the paywall,
           but keep the sidebar (with its community switcher) mounted alongside. -->
      <div class="flex flex-1 items-center justify-center px-6 py-12">
        <ExpiredPaywall skoolId={active.id} communityName={active.name} status={active.plan_status as 'expired' | 'needs_payment'} />
      </div>
    {:else}
      {@render children()}
    {/if}
  </div>
</div>
